import { DeviceType } from '@/src/types'
import * as apiService from './apiService'
import { AxiosResponse } from 'axios'
import { VisaItineraryData } from '@/src/types/visa/visaItinerary'
import { BookingDetails } from '@/src/types/visa/bookingDetails'
import { UserProfileResponse } from '@/src/types/visa/userProfile'
import { VISA_ERRORS } from '@/src/constants/error'
import { handleApiError } from '@/src/utils/errorHandler'
import { isLoggedIn } from '@/src/utils/helper'
import {
    CouponListResponse,
    ApplyCouponRequest,
    RemoveCouponRequest,
    CouponResponse
} from '@/src/types/visa/coupon'

export async function getVisaOptions(
    payload: object,
    deviceType: DeviceType
): Promise<AxiosResponse<any>> {
    try {
        const URL = '/api/visa/country-metadata'
        const response = await apiService.request(
            URL,
            payload,
            deviceType,
            'post',
            { skipErrorHandling: true }
        )
        return response as AxiosResponse<any>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR)
        throw e
    }
}

export async function createItinerary(
    payload: object,
    deviceType: DeviceType
): Promise<AxiosResponse<VisaItineraryData>> {
    try {
        const URL = '/api/visa/info'
        const response = await apiService.request<VisaItineraryData>(
            URL,
            payload,
            deviceType,
            'post',
            { skipErrorHandling: true }
        )
        return response as AxiosResponse<VisaItineraryData>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.ITINERARY_CREATE_ERROR)
        throw e
    }
}

export async function viewItinerary(
    itineraryId: string,
    deviceType: DeviceType
): Promise<AxiosResponse<VisaItineraryData>> {
    try {
        const URL = `/api/visa/info/${itineraryId}`
        const response = await apiService.get<VisaItineraryData>(
            URL,
            deviceType,
            { skipErrorHandling: true }
        )
        return response as AxiosResponse<VisaItineraryData>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_BOOKING_ERROR)
        throw e
    }
}

export async function updateItinerary(
    payload: object,
    itineraryId: string,
    deviceType: DeviceType
): Promise<AxiosResponse<VisaItineraryData>> {
    try {
        const URL = `/api/visa/info/${itineraryId}`
        const response = await apiService.request<VisaItineraryData>(
            URL,
            payload,
            deviceType,
            'put',
            { skipErrorHandling: true }
        )
        return response as AxiosResponse<VisaItineraryData>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.ITINERARY_UPDATE_ERROR)
        throw e
    }
}

export async function initiatePayment(
    itineraryId: string,
    deviceType: DeviceType
): Promise<AxiosResponse<any>> {
    try {
        const URL = `/api/visa/prepayment/${itineraryId}`
        const response = await apiService.request(URL, {}, deviceType, 'post', {
            skipErrorHandling: true
        })
        return response as AxiosResponse<any>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.PAYMENT_INITIATION_ERROR)
        throw e
    }
}

export async function getVisaBookingList(deviceType: DeviceType = 'DESKTOP') {
    try {
        const URL = '/api/visa/bookings'
        const response = await apiService.get(URL, deviceType, {
            skipErrorHandling: true
        })
        if (
            response &&
            typeof response === 'object' &&
            response !== null &&
            'data' in response
        ) {
            return response.data
        }
        throw new Error('Invalid response from API')
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.BOOKING_LIST_ERROR)
        throw e
    }
}

export async function getBookingDetails(
    itineraryId: string,
    deviceType: DeviceType = 'DESKTOP'
) {
    try {
        if (isLoggedIn()) {
            const URL = `/api/visa/bookingdetails/${itineraryId}`
            const response = await apiService.get(URL, deviceType, {
                skipErrorHandling: true,
                contentType: 'application/json'
            })

            if (
                response &&
                typeof response === 'object' &&
                'data' in response
            ) {
                const data = response.data as any
                const responseData = data.response || data
                return responseData
            }
            throw new Error('Invalid response from API')
        }
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_BOOKING_ERROR)
        throw e
    }
}

export async function getVisaBookings(
    itineraryId: string,
    deviceType: DeviceType = 'DESKTOP'
) {
    try {
        if (isLoggedIn()) {
            const URL = `/api/visa/bookings/${itineraryId}`
            const response = await apiService.get(URL, deviceType, {
                skipErrorHandling: true
            })
            if (
                response &&
                typeof response === 'object' &&
                'data' in response
            ) {
                return response.data
            }
            throw new Error('Invalid response from API')
        }
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_BOOKING_ERROR)
        throw e
    }
}

export async function uploadDocuments(
    itineraryId: string,
    formData: FormData,
    deviceType: DeviceType = 'DESKTOP'
) {
    try {
        const URL = `/api/visa/upload/${itineraryId}`
        const response = await apiService.request(
            URL,
            formData,
            deviceType,
            'post',
            {
                skipErrorHandling: true,
                contentType: 'multipart/form-data'
            }
        )

        if (response && typeof response === 'object' && 'data' in response) {
            return response.data
        }
        throw new Error('Invalid response from API')
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.DOCUMENT_UPLOAD_ERROR)
        throw e
    }
}

export async function getVisaPayConfirmData(
    itineraryId: string,
    deviceType: DeviceType = 'DESKTOP'
) {
    try {
        const URL = `/api/visa/confirmation/${itineraryId}`
        const response = await apiService.get(URL, deviceType, {
            skipErrorHandling: true
        })
        if (response && typeof response === 'object' && 'data' in response) {
            return response.data
        }
        throw new Error('Invalid response from API')
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.PAYMENT_CONFIRMATION_ERROR)
        throw e
    }
}

// User Profile API functions
export async function getUserProfile(
    deviceType: DeviceType = 'DESKTOP'
): Promise<AxiosResponse<UserProfileResponse>> {
    try {
        const URL = '/accounts-api/people/v2/fetch/details'
        const response = await apiService.get<UserProfileResponse>(
            URL,
            deviceType,
            {
                skipErrorHandling: true,
                requestHeaders: {
                    caller: 'visa-flow'
                }
            }
        )
        return response as AxiosResponse<UserProfileResponse>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR)
        throw e
    }
}

// Coupon API functions
export async function getCoupons(
    deviceType: DeviceType = 'DESKTOP'
): Promise<AxiosResponse<CouponListResponse>> {
    try {
        const URL = '/api/visa/coupon'
        const response = await apiService.get<CouponListResponse>(
            URL,
            deviceType,
            {
                skipErrorHandling: true
            }
        )
        return response as AxiosResponse<CouponListResponse>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR)
        throw e
    }
}

export async function applyCoupon(
    itineraryId: string,
    payload: ApplyCouponRequest,
    deviceType: DeviceType = 'DESKTOP'
): Promise<AxiosResponse<CouponResponse>> {
    try {
        const URL = `/api/visa/coupon/${itineraryId}`
        const response = await apiService.request<CouponResponse>(
            URL,
            payload,
            deviceType,
            'post',
            { skipErrorHandling: true }
        )
        return response as AxiosResponse<CouponResponse>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR)
        throw e
    }
}

export async function removeCoupon(
    itineraryId: string,
    payload: RemoveCouponRequest,
    deviceType: DeviceType = 'DESKTOP'
): Promise<AxiosResponse<CouponResponse>> {
    try {
        const URL = `/api/visa/coupon/${itineraryId}`
        const response = await apiService.request<CouponResponse>(
            URL,
            payload,
            deviceType,
            'post',
            { skipErrorHandling: true }
        )
        return response as AxiosResponse<CouponResponse>
    } catch (e: any) {
        handleApiError(e, VISA_ERRORS.FETCH_VISA_REQUIREMENTS_ERROR)
        throw e
    }
}
